import React, { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, Trash2, Check, Mail, Pencil, X } from "lucide-react";
import { Input } from "@cms/ui/components/input";
import { Button } from "@cms/ui/components/button";
import { Card, CardHeader, CardTitle, CardContent } from "@cms/ui/components/card";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import { AddPortalUserForm } from "./add-portal-user-form";
import { InviteLinkBox, type InviteDetails } from "./invite-link-box";
import type { ApiClient } from "@cms/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";

interface PortalUser {
  user_id: string;
  email: string;
  display_name: string | null;
  created_at?: string;
}

export function UsersPanel({ client, api, onUsersChanged }: {
  client: AdminClient;
  api: ApiClient | null;
  onUsersChanged?: (count: number) => void;
}) {
  const [users, setUsers] = useState<PortalUser[] | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PortalUser | null>(null);
  // Inline display-name edit: { userId, value } while a rename is open.
  const [nameEdit, setNameEdit] = useState<{ userId: string; value: string } | null>(null);
  const [savingName, setSavingName] = useState(false);
  // Ref so an unmemoized parent callback doesn't re-trigger the load effect.
  const onUsersChangedRef = useRef(onUsersChanged);
  onUsersChangedRef.current = onUsersChanged;

  const loadUsers = useCallback(async () => {
    if (!api) return;
    try {
      const body = await api.request<{ users: PortalUser[] }>(`/admin/clients/${client.id}/users`);
      setUsers(body.users);
      onUsersChangedRef.current?.(body.users.length);
    } catch (err) {
      setError((err as Error).message);
      setUsers([]);
    }
  }, [api, client.id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const confirmRemove = async () => {
    const user = pendingDelete;
    setPendingDelete(null);
    if (!api || !user) return;
    setRemoving(user.user_id);
    setError("");

    try {
      await api.request(`/admin/clients/${client.id}/users/${user.user_id}`,
        { method: "DELETE" }
      );
      if (invite?.email === user.email) setInvite(null);
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRemoving(null);
    }
  };

  const regenerateInvite = async (user: PortalUser) => {
    if (!api) return;
    setInviting(user.user_id);
    setError("");
    setInvite(null);

    try {
      const body = await api.request<{ inviteLink: string | null }>(
        `/admin/clients/${client.id}/users/${user.user_id}/invite`,
        { method: "POST" }
      );
      setInvite({ userId: user.user_id, email: user.email, link: body.inviteLink });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setInviting(null);
    }
  };

  // Display names drive post bylines (live-resolved by user id), so a rename
  // here updates the client's blog on their next publish.
  const saveDisplayName = async () => {
    if (!api || !nameEdit) return;
    setSavingName(true);
    setError("");
    try {
      await api.request(`/admin/clients/${client.id}/users/${nameEdit.userId}`,
        { method: "PATCH", body: JSON.stringify({ display_name: nameEdit.value.trim() || null }) }
      );
      setNameEdit(null);
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Portal users</CardTitle>
      </CardHeader>
      <CardContent>
        {users === null ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No portal users yet — add one below to give this client access.
          </p>
        ) : (
          <ul className="space-y-2">
            {users.map((user) => (
              <li
                key={user.user_id}
                className="flex items-center justify-between gap-4 rounded-lg border p-3.5"
              >
                <div className="min-w-0">
                  {nameEdit?.userId === user.user_id ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={nameEdit.value}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNameEdit({ userId: user.user_id, value: event.target.value })}
                        placeholder="Display name"
                        className="h-7 max-w-48 text-sm"
                        autoFocus
                      />
                      <Button size="sm" variant="ghost" disabled={savingName} onClick={saveDisplayName} aria-label="Save name">
                        {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </Button>
                      <Button size="sm" variant="ghost" disabled={savingName} onClick={() => setNameEdit(null)} aria-label="Cancel rename">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                      {user.display_name || user.email}
                      <button
                        type="button"
                        onClick={() => setNameEdit({ userId: user.user_id, value: user.display_name ?? "" })}
                        className="text-muted-foreground hover:text-foreground"
                        title="Edit display name (shown on blog posts)"
                        aria-label="Edit display name"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </p>
                  )}
                  {user.display_name && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}
                  <p className="font-mono text-xs text-muted-foreground truncate">{user.user_id}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={inviting === user.user_id}
                    onClick={() => regenerateInvite(user)}
                  >
                    {inviting === user.user_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Mail className="h-3.5 w-3.5" />
                    )}
                    Invite
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={removing === user.user_id}
                    onClick={() => setPendingDelete(user)}
                  >
                    {removing === user.user_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {invite && api && (
          <InviteLinkBox
            api={api}
            clientId={client.id}
            invite={invite}
            onLinkChange={(link) => setInvite((current) => (current ? { ...current, link } : current))}
          />
        )}

        <AddPortalUserForm
          client={client}
          api={api}
          onAdded={loadUsers}
          className="mt-4"
        />

        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        {!api && (
          <p className="mt-3 text-xs text-muted-foreground">
            No API configured — users cannot be managed.
          </p>
        )}
      </CardContent>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open: boolean) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove portal user?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.email} will no longer be able to log in to this client's portal.
              This can't be undone, but you can re-invite them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
